// Curated root `--help` output for the CloudGrid CLI.
//
// The default commander help lists every registered command in one flat block,
// ordered by registration and using each command's raw `.description()`. That
// reads as a wall of 38 verbs with no hierarchy. This module replaces the ROOT
// program's help with a hand-authored, grouped layout (Basic / Advanced / Auth /
// Meta) plus orientation, a cloudgrid.yaml primer, and worked examples.
//
// IMPORTANT — do NOT source these command blurbs from the per-command
// `.description()` strings. `docs/cli-reference.md` (the `cli-drift-gate` CI
// guard) is generated from those descriptions; the wording here is deliberately
// its own copy so the two never couple. The curated blurbs below are the source
// of truth for this screen only.
//
// This is applied at the root program only (see program.ts,
// `configureHelp({ formatHelp })`). Subcommand help (`<cmd> --help`,
// `help <cmd>`) still uses commander's default formatter.
//
// §23 voice: plain, specific, confident, no marketing adjectives, no emoji, no
// exclamation marks.

/**
 * Every top-level command name referenced in the curated layout below.
 *
 * The guard test (`root-help.test.ts`) asserts this set matches the registered
 * non-hidden commands of `buildProgram()`, so a newly added command can't be
 * silently omitted from this screen. Keep it in sync with the layout text.
 */
export const CURATED_COMMANDS: readonly string[] = [
  // Basic
  'new',
  'plug',
  'status',
  'logs',
  'open',
  'info',
  'unplug',
  'dev',
  'rollback',
  'fork',
  'remix',
  'pickup',
  'pull',
  'download',
  'agent',
  // Advanced
  'get',
  'create',
  'describe',
  'delete',
  'rename',
  'visibility',
  'versions',
  'follow',
  'link',
  'wire',
  'env',
  'secrets',
  'vault',
  'scaffold',
  'space',
  'domains',
  'admin',
  'hooks',
  'callers',
  'calls',
  'use',
  'ssh',
  // Auth
  'login',
  'logout',
  'whoami',
  // Meta
  'version',
  'doctor',
  'completion',
  'feedback',
  'recipes',
];

/**
 * The curated help body. The `{{cli}}` placeholder marks every spot that names
 * the *invoker* — the header, usage line, the two hint lines, and each `$ …`
 * example — and is substituted with the actual binary name (`grid`) by
 * `renderRootHelp`. Literal product tokens are NOT placeholders and must stay
 * verbatim: the `cloudgrid.yaml` filename and the "CloudGrid team" proper noun.
 */
const ROOT_HELP_TEMPLATE = `{{cli}} — plug your apps and agents to the grid

{{cli}} <command> [options]

The CLI verb is \`grid\`.

For command help, run \`{{cli}} help <command>\`

Using an AI coding agent? Run \`{{cli}} agent\` to write playbook
instructions (AGENTS.md + CLAUDE.md) into this folder so Claude Code,
Cursor, or Codex knows the golden path automatically.

Commands:

  Basic

    new          [name]             Set up a folder to build an app or agent: scaffolds
                                     cloudgrid.yaml and a starter service. Mints nothing until
                                     you plug or dev (\`new service <name>\` adds a service)
    plug         [target]           Build + deploy + go live and print the share URL;
                                     re-run after any change to update the live deploy
    status       [name-or-trace]    No args: dashboard of everything on your grid. With a
                                     name: that entity's state. With a trace id: deploy progress
    logs         [name]             Stream runtime logs from the live entity (defaults to
                                     the entity linked to this folder)
    open         [name]             Open the entity's live URL in the default browser
    info         [name]             Show entity metadata: URL, org, visibility, services
    unplug       [name]             Take an entity off the grid (its URL stops serving)
    dev          [service...]       Run the app locally with every \`needs:\` resource from
                                     cloudgrid.yaml tunneled in — no Docker, no local
                                     MongoDB/Redis installs; env vars are injected automatically
    rollback     [name]             Re-point the live deploy at a previous successful
                                     version (env vars and secrets are untouched)
    fork         [id|org/slug]      Copy someone else's entity into your grid as a new node
    remix        [id|grid/slug]     Make your own copy of an app you can see in a grid you
                                     choose (cross-grid), minus the source's secrets
    pickup       [name] [dir]       Adopt an existing entity: download its source and link
                                     the folder to it so \`plug\` updates it
    pull         [name] [dir]       Get your app's source and link the folder so \`plug\`
                                     updates it (alias of \`pickup\`)
    download     [name]             Download the exact source bundle of the last deploy
    agent                           Write AI agent instructions (AGENTS.md + CLAUDE.md)
                                     into this folder; prompts before writing

  Advanced

    get                             List grids, entities, or spaces (\`get entities\`)
    create                          Create a new grid
    describe                        Show full details of a grid or entity
    delete                          Delete a grid or archive an entity
    rename       <slug> <name>      Change an entity's display name (slug and URL unchanged)
    visibility                      Get/set who can open the URL: private | space | org | link
    versions     [id]               List every version minted by \`plug\`; tag, pin, or unpin
    follow       [trace_id]         Re-attach to an in-flight deploy and stream progress
    link         [entity]           Bind this folder to an existing entity (alias of \`wire folder\`)
    wire                            Bind a folder, a git repo, or a webhook to an entity
    env                             Runtime env vars: list / get / set KEY=VAL / unset
                                     (updates trigger a rolling restart)
    secrets                         Runtime secrets: set / unset (values are write-only,
                                     never printed back)
    vault                           Grid vault items: set / list / rotate (values are
                                     write-only, never printed back)
    scaffold                        Re-scan cloudgrid.yaml and write any missing service
                                     starter files (idempotent; existing files untouched)
    space                           Manage spaces (sub-groupings inside a grid)
    domains                         Serve apps on your own domain (add / list / verify / remove)
    admin                           Admin operations on the active grid
    hooks                           Manage webhook hooks
    callers                         Who may call an app you own: list / approve / revoke
    calls                           What an app of yours calls: list / withdraw
    use          [slug]             Switch the active grid; no arg prints the current one
    ssh          [name]             Interactive shell inside the running pod (no kubectl needed)

  Auth

    login                           Sign in via Google OAuth (opens the browser)
    logout                          Sign out and clear local credentials
    whoami                          Show the signed-in user and active grid

  Meta

    version                         Show CLI version
    doctor                          Diagnose the local setup: Node, auth, API reachability
    completion   [shell]            Emit shell completion script (zsh | bash | fish)
    feedback     [message]          Send feedback to the CloudGrid team
    recipes      [name]             Print Runtime SDK recipes (the agent cookbook)
    help         [command]          Full help for any command

Global Options:

  -h, --help                        Output usage information
  -V, --version                     Output the version number
  -v, --verbose                     Show detailed output [off]

The cloudgrid.yaml:

  One file describes the whole app — services plus everything they need.
  Declare a database, cache, queue, or vector store under \`needs:\` and the
  grid provisions it and injects the connection env var; nothing to install.


Examples:

– Create an app and make it live

  $ {{cli}} new my-api --type node    # scaffold in the current folder (nothing on the grid yet)
  $ {{cli}} plug                      # mint the entity and deploy

– Add a database and a cache: declare them in cloudgrid.yaml (see above),
  then redeploy — the grid provisions them and injects MONGODB_URL / REDIS_URL

  $ {{cli}} plug

– Run locally against real grid resources — no Docker, no installing
  MongoDB/Redis/Supabase; every \`needs:\` entry is tunneled in with its
  env var already set

  $ {{cli}} dev

– Need another service, or another resource? Edit cloudgrid.yaml (add a
  service under \`services:\` or a line under \`needs:\`), then just run
  \`dev\` again.

  $ {{cli}} dev

– Check a deploy, then roll back if it's bad

  $ {{cli}} status my-api
  $ {{cli}} rollback my-api

– Give an AI agent everything it needs to work in this repo

  $ {{cli}} agent
`;

/**
 * Render the curated root help text for the given invoker name. Every `{{cli}}`
 * placeholder — header, usage, hint lines, and every `$ …` example — is
 * substituted with `invokedAs` so `grid --help` shows `grid …`. The literal
 * `cloudgrid.yaml` filename and the "CloudGrid team" proper noun are authored
 * without the placeholder and stay verbatim.
 *
 * When `invokedAs` is falsy, or `index.js`/`index` (the raw entry basename seen
 * when the CLI runs from `node dist/index.js` rather than an installed bin), we
 * fall back to `grid` so the screen never advertises the entry filename.
 */
export function renderRootHelp(invokedAs: string): string {
  const raw = invokedAs || '';
  const name = raw && raw !== 'index.js' && raw !== 'index' ? raw : 'grid';
  return ROOT_HELP_TEMPLATE.replace(/\{\{cli\}\}/g, name);
}
